import { useEffect, useState } from "react";
import { X, Loader2, Trash2, Camera, MapPin, Compass, Clock } from "lucide-react";
import api, { API } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Mono, token } from "./ui";

const fileUrl = (id) => `${API}/fieldclaim/photos/${id}/file?token=${token()}`;

export function PhotoCard({ photo, onDelete }) {
  const remove = async () => { await api.delete(`/fieldclaim/photos/${photo.id}`); onDelete(); };
  return (
    <div className="relative group border border-gold-500/20 bg-navy-950" data-testid={`photo-${photo.id}`}>
      <img src={fileUrl(photo.id)} alt="evidence" className="w-full h-32 object-cover" />
      <button onClick={remove} data-testid={`delete-photo-${photo.id}`} className="absolute top-1.5 right-1.5 bg-navy-950/80 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-white/70 hover:text-red-400">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      <div className="p-2 space-y-1">
        {photo.latitude != null && photo.longitude != null ? (
          <div className="flex items-center gap-1 text-[0.55rem] font-mono text-gold-300/80"><MapPin className="h-3 w-3" /> {Number(photo.latitude).toFixed(5)}, {Number(photo.longitude).toFixed(5)}</div>
        ) : (
          <div className="text-[0.55rem] font-mono text-amber-400/80">No GPS</div>
        )}
        {photo.chainage && <div className="text-[0.55rem] font-mono text-white/45">Ch {photo.chainage}</div>}
        {photo.note && <div className="text-[0.6rem] text-white/55 line-clamp-2">{photo.note}</div>}
      </div>
    </div>
  );
}

export function PhotoCaptureModal({ capture, assetId, phaseName, defaultChainage, onClose, onUploaded }) {
  const [preview, setPreview] = useState("");
  const [geo, setGeo] = useState({ status: "locating", latitude: null, longitude: null, accuracy: null });
  const [bearing, setBearing] = useState("");
  const [chainage, setChainage] = useState(defaultChainage);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const url = URL.createObjectURL(capture.file);
    setPreview(url);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({ status: "ok", latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => setGeo({ status: "denied", latitude: null, longitude: null, accuracy: null }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setGeo({ status: "unsupported", latitude: null, longitude: null, accuracy: null });
    }
    return () => URL.revokeObjectURL(url);
  }, [capture]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const compressed = await compressImage(capture.file, { maxDim: 1600, quality: 0.7 });
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("phase", capture.phase);
      fd.append("taken_at", new Date().toISOString());
      if (geo.latitude != null) { fd.append("latitude", geo.latitude); fd.append("longitude", geo.longitude); fd.append("gps_accuracy", geo.accuracy ?? ""); }
      if (bearing !== "") fd.append("bearing", bearing);
      if (chainage) fd.append("chainage", chainage);
      if (note) fd.append("note", note);
      await api.post(`/fieldclaim/assets/${assetId}/photos`, fd);
      onUploaded();
    } catch (e) {
      setErr("Upload failed. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-md max-h-[92vh] overflow-y-auto" data-testid="photo-capture-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
          <div>
            <Mono className="text-gold-400 text-[0.55rem]">Capture evidence</Mono>
            <div className="font-heading text-lg">{phaseName}</div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        {preview && <img src={preview} alt="preview" className="w-full h-56 object-cover" />}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-gold-500/20 bg-navy-950 p-3">
              <div className="flex items-center gap-1.5 text-[0.55rem] font-mono uppercase tracking-widest text-white/40"><MapPin className="h-3 w-3" /> GPS</div>
              <div className="mt-1.5 text-sm font-mono" data-testid="capture-gps">
                {geo.status === "locating" && <span className="text-white/50 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Locating…</span>}
                {geo.status === "ok" && <span className="text-gold-300">{geo.latitude.toFixed(5)}, {geo.longitude.toFixed(5)}<span className="text-white/40 text-[0.6rem] block">±{Math.round(geo.accuracy)}m</span></span>}
                {(geo.status === "denied" || geo.status === "unsupported") && <span className="text-amber-400 text-xs">Unavailable</span>}
              </div>
            </div>
            <div className="border border-gold-500/20 bg-navy-950 p-3">
              <div className="flex items-center gap-1.5 text-[0.55rem] font-mono uppercase tracking-widest text-white/40"><Clock className="h-3 w-3" /> Timestamp</div>
              <div className="mt-1.5 text-sm font-mono text-gold-300">{new Date().toLocaleString("en-AU", { hour12: false })}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="flex items-center gap-1.5 text-[0.55rem] font-mono uppercase tracking-widest text-white/40 mb-1.5"><Compass className="h-3 w-3" /> Bearing (°)</span>
              <input type="number" min="0" max="360" value={bearing} onChange={(e) => setBearing(e.target.value)} data-testid="capture-bearing" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500" />
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-mono uppercase tracking-widest text-white/40 mb-1.5 block">Chainage</span>
              <input value={chainage} onChange={(e) => setChainage(e.target.value)} data-testid="capture-chainage" placeholder="1+450" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500" />
            </label>
          </div>
          <label className="block">
            <span className="text-[0.55rem] font-mono uppercase tracking-widest text-white/40 mb-1.5 block">Site note</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="capture-note" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500 resize-none" />
          </label>
          {err && <div className="text-red-400 text-sm" data-testid="capture-error">{err}</div>}
          <button onClick={save} disabled={saving || geo.status === "locating"} data-testid="save-photo-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Save evidence
          </button>
        </div>
      </div>
    </div>
  );
}
